import React, { createContext, useState, useEffect, useContext } from 'react'
import { supabase } from './supabaseClient'
import { logAuditEvent, AUDIT_ACTIONS } from './utils/auditLog'

const AuthContext = createContext({})

function getCachedUser() {
  try {
    const key = Object.keys(localStorage).find(k => k.endsWith('-auth-token'))
    if (key) {
      const raw = JSON.parse(localStorage.getItem(key))
      return raw?.user ?? null
    }
  } catch {}
  return null
}

export const AuthProvider = ({ children }) => {
  const cachedUser = getCachedUser()
  const [user, setUser] = useState(cachedUser)
  const [loading, setLoading] = useState(!cachedUser)

  useEffect(() => {
    // Check if this is a password recovery flow
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const type = hashParams.get('type')

    if (type === 'recovery') {
      // Don't set user, let Login component handle password reset
      setLoading(false)
      return
    }

    // Check active sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  }

  const signOut = async () => {
    try {
      await logAuditEvent(AUDIT_ACTIONS.LOGOUT, {})
    } catch {}
    try {
      // Sign out with scope 'local' to clear localStorage
      await supabase.auth.signOut({ scope: 'local' })
    } catch (error) {
      console.log('Sign out error:', error)
    }

    // Clear user state
    setUser(null)

    // Clear all storage, except inspection drafts (keyed per user) and
    // per-device preferences, which are set aside and restored
    const preserved = {}
    try {
      Object.keys(localStorage).forEach(key => {
        if (
          key.startsWith('inspection_draft:') ||
          key.startsWith('sms_consent_') ||
          key === 'darkMode' ||
          key === 'abc_sidebar_collapsed'
        ) {
          preserved[key] = localStorage.getItem(key)
        }
      })
    } catch {}
    localStorage.clear()
    sessionStorage.clear()
    try {
      Object.entries(preserved).forEach(([key, value]) => localStorage.setItem(key, value))
    } catch {}

    // Force a hard reload with cache busting
    window.location.replace('/?t=' + Date.now())
  }

  const value = {
    user,
    signIn,
    signOut,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}